"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import {
  CollectionsRichText,
  type RichTextSegment,
} from "@/components/landing/CollectionsRichText";
import { Icon, ICON_TYPE } from "@/components/library/Icon";
import { joinClasses } from "@/puck/landing/shared";
import type {
  LpBuyerQuoteBlockProps,
  LpFaqBlockProps,
  LpHonestFilterBlockProps,
  LpOutcomesBlockProps,
} from "@/puck/types";

export function LpOutcomesBlock({
  eyebrow,
  titleLead,
  titleAccent,
  subtitle,
  attributionSeparator,
  background,
  items,
}: Readonly<LpOutcomesBlockProps>) {
  return (
    <section
      className={joinClasses(
        "w-full border-t border-black/10 px-4 pt-20 pb-20 md:pt-28 md:pb-28",
        background === "voiceCard" && "bg-marketing-voice-card",
      )}
    >
      <div className="mx-auto w-full max-w-3xl text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
          {titleLead} <span className="text-gail-primary">{titleAccent}</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl font-monument text-base leading-relaxed text-gail-gray md:text-lg">
          {subtitle}
        </p>
      </div>

      <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-4 md:mt-16 md:grid-cols-3">
        {items.map((outcome, index) => (
          <div
            key={`outcome-${index}`}
            className="flex flex-col rounded-2xl border border-black/10 bg-white px-6 py-7 md:px-7"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-gail-primary">
              {outcome.eyebrow}
            </p>
            <p className="mt-4 font-monument text-5xl font-bold leading-none tracking-tight text-gail-primary md:text-6xl">
              {outcome.stat}
              {outcome.statSuffix ? (
                <span className="text-2xl text-black md:text-3xl">
                  {outcome.statSuffix}
                </span>
              ) : null}
            </p>
            <p className="mt-4 font-monument text-sm leading-relaxed text-gail-gray md:text-[15px]">
              {outcome.captionLead}
              {outcome.captionHighlight ? (
                <span className="font-bold text-gail-primary">
                  {outcome.captionHighlight}
                </span>
              ) : null}
              {outcome.captionTail || null}
            </p>
            <p className="mt-5 font-monument text-base leading-relaxed text-black md:text-[17px]">
              &ldquo;{outcome.quote}&rdquo;
            </p>
            <p className="mt-6 border-t border-black/10 pt-4 font-monument text-sm text-gail-gray md:mt-auto">
              <span className="font-bold text-black">
                {outcome.attributionName}
              </span>
              {attributionSeparator}
              {outcome.attributionRole}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function LpBuyerQuoteBlock({
  eyebrow,
  titleLead,
  titleAccent,
  subtitle,
  quote,
  attributionName,
  attributionRole,
}: Readonly<LpBuyerQuoteBlockProps>) {
  return (
    <section className="w-full border-t border-black/10 bg-marketing-voice-card px-4 pt-20 pb-20 md:pt-28 md:pb-28">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-12">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
            {eyebrow}
          </p>
          <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-5xl">
            {titleLead} <span className="text-gail-primary">{titleAccent}</span>
          </h2>
          <p className="mt-6 max-w-md font-monument text-base leading-relaxed text-gail-gray md:text-lg">
            {subtitle}
          </p>
        </div>

        <figure className="relative flex min-h-[320px] flex-col justify-between overflow-hidden rounded-3xl bg-[linear-gradient(155deg,#2f7bf0_0%,#1565d8_46%,#0d4eb0_100%)] px-8 py-8 shadow-[0_24px_60px_-20px_rgba(13,78,176,0.55)] md:px-10 md:py-10">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_30%,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0)_60%)]"
          />

          <span
            aria-hidden="true"
            className="relative font-monument text-6xl font-bold leading-none text-white/30"
          >
            &ldquo;
          </span>

          <div className="relative rounded-xl bg-gail-secondary px-5 py-5 md:px-6">
            <blockquote className="font-monument text-2xl font-bold leading-snug tracking-tight text-white md:text-3xl">
              {quote}
            </blockquote>

            <figcaption className="mt-6">
            <p className="font-monument text-base font-bold text-white">
              {attributionName}
            </p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-white">
              {attributionRole}
            </p>
            </figcaption>
          </div>
        </figure>
      </div>
    </section>
  );
}

export function LpHonestFilterBlock({
  eyebrow,
  titleLead,
  titleAccent,
  background,
  columns,
}: Readonly<LpHonestFilterBlockProps>) {
  return (
    <section
      className={joinClasses(
        "w-full border-t border-black/10 px-4 pt-20 pb-20 md:pt-28 md:pb-28",
        background === "voiceCard" && "bg-marketing-voice-card",
      )}
    >
      <div className="mx-auto w-full max-w-3xl text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
          {titleLead} <span className="text-gail-primary">{titleAccent}</span>
        </h2>
      </div>

      <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-4 md:mt-16 md:grid-cols-2">
        {columns.map((column, index) => (
          <div
            key={`column-${index}`}
            className="rounded-2xl border border-black/10 bg-white px-6 py-7 md:px-8"
          >
            <div className="flex items-center gap-3">
              <h3 className="font-monument text-lg font-bold tracking-tight text-black">
                {column.title}
              </h3>
              <span
                className={joinClasses(
                  "rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]",
                  column.variant === "yes"
                    ? "bg-gail-primary-light text-gail-primary"
                    : "bg-black/5 text-gail-gray",
                )}
              >
                {column.badge}
              </span>
            </div>

            <ul className="mt-5 flex flex-col gap-4">
              {column.items.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span
                    className={joinClasses(
                      "mt-0.5 shrink-0",
                      column.variant === "yes"
                        ? "text-gail-primary"
                        : "text-black/30",
                    )}
                  >
                    <Icon
                      icon={column.variant === "yes" ? "check" : "xmark"}
                      type={ICON_TYPE.SOLID}
                      className="h-4 w-4"
                    />
                  </span>
                  <p className="font-monument text-sm leading-relaxed text-gail-gray md:text-[15px]">
                    <CollectionsRichText
                      segments={item.segments as RichTextSegment[]}
                    />
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function LpFaqBlock({
  eyebrow,
  titleLead,
  titleAccent,
  background,
  items,
}: Readonly<LpFaqBlockProps>) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      className={joinClasses(
        "w-full border-t border-black/10 px-4 pt-20 pb-20 md:pt-28 md:pb-28",
        background === "surface"
          ? "bg-marketing-surface"
          : "bg-marketing-voice-card",
      )}
    >
      <div className="mx-auto w-full max-w-3xl text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
          {titleLead} <span className="text-gail-primary">{titleAccent}</span>
        </h2>
      </div>

      <div className="mx-auto mt-12 w-full max-w-3xl md:mt-16">
        {items.map((item, index) => {
          const isOpen = openIndex === index;

          return (
            <div key={`faq-${index}`} className="border-t border-black/10">
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-6 py-6 text-left"
              >
                <span className="font-monument text-base text-black md:text-lg">
                  {item.question}
                </span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/5 text-gail-gray">
                  <Icon
                    icon="plus"
                    type={ICON_TYPE.SOLID}
                    className={joinClasses(
                      "h-3 w-3 transition-transform duration-300 ease-out",
                      isOpen && "rotate-45",
                    )}
                  />
                </span>
              </button>

              <AnimatePresence initial={false}>
                {isOpen ? (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="max-w-2xl pb-6 font-monument text-base leading-relaxed text-gail-gray">
                      {item.answer}
                    </p>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          );
        })}
        <div className="border-t border-black/10" />
      </div>
    </section>
  );
}
